import { Injectable } from '@angular/core';
import { LeadsDataServiceProvider } from '@services/lead/leads.data.service';
import { TimeConverterProvider } from '@services/helpers/time-converter';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { ValidationProvider } from '@services/helpers/validation.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { ClientServiceProvider } from '@services/client/client.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { EmailServiceProvider } from '@services/email/email-service/email.service';

@Injectable()
export class LeadServiceProvider {
	constructor(
		private stringModifierService: StringModifierServiceProvider,
		private leadDataService: LeadsDataServiceProvider,
		private validationService: ValidationProvider,
		private messageService: MessageService,
		private timeService: TimeServiceProvider,
		private timeConverter: TimeConverterProvider,
		private clientService: ClientServiceProvider,
		private emailService: EmailServiceProvider
	) {}

	private isContact(contact): boolean {
		const contactExist = contact && contact.id;
		return !!contactExist;
	}

	private isContactsEqual(secondaryContact, primaryContact): boolean {
		if (secondaryContact.id === primaryContact.id) {
			this.messageService.showToaster('e_5', 'job');
			return true;
		}
		return false;
	}

	prepareLead(job, jobTypes) {
		const job2Edit = JSON.parse(JSON.stringify(job));

		const { workflow, startDate, finishDate } = job2Edit;

		job2Edit.oldWorkflow = workflow;

		if (!job2Edit.startDate) {
			job2Edit.hasNoDate = true;
		} else {
			job2Edit.hasNoDate = false;
			job2Edit.startDate = this.timeService.createDateAsUTC(startDate);
			job2Edit.finishDate = this.timeService.createDateAsUTC(finishDate);
		}

		if (job2Edit.workflow && job2Edit.workflow.name) {
			job2Edit.workflow.name = this.stringModifierService.addSelectedText(
				job2Edit.workflow.name
			);
		}

		if (
			job2Edit.startDate &&
			(job2Edit.startTime === null || job2Edit.finishTime === null)
		) {
			job2Edit.allday = true;
		}

		if (jobTypes) {
			job2Edit.type = jobTypes.find(item => item.id === job2Edit.type.id);
		}

		return job2Edit;
	}

	addClients(job) {
		const { primaryContact, secondaryContact } = job;
		const clients = [];

		if (typeof primaryContact !== 'undefined' && primaryContact !== null) {
			const primaryClientSearchText = this.clientService.fillClientSearchText(
				primaryContact
			);
			clients.push(primaryClientSearchText);
		}

		if (typeof secondaryContact !== 'undefined' && secondaryContact !== null) {
			const secondaryClientSearchText = this.clientService.fillClientSearchText(
				secondaryContact
			);
			clients.push(secondaryClientSearchText);
		}

		return clients;
	}

	checkWorkflowName(job, workflowNames) {
		if (typeof workflowNames !== 'undefined' && workflowNames.length) {
			const isWorkflowSaved = workflowNames.find(
				workflow => workflow.id === job.workflow.id
			);
			const workflowNamesCopy = workflowNames.slice();
			if (!isWorkflowSaved) {
				workflowNamesCopy.push(job.workflow);
				return workflowNamesCopy;
			}
			return workflowNames;
		}
		return workflowNames;
	}

	getTaskList(job: any) {
		if (!job.workflow) {
			return false;
		}

		let taskList = job.workflow.tasks.slice();

		taskList = taskList.sort((a, b) => a.number > b.number);

		return taskList;
	}

	animateNewTask(taskList, showNewTaskId) {
		setTimeout(() => {
			const newTask = taskList.find(task => task.id === showNewTaskId);
			const taskToAnimate = `.task-${newTask.number}`;
			// $rootScope.$broadcast('animateTask', taskToAnimate);
		}, 0);
	}

	validateJob(job, clients, leadId, jobId?) {
		let dateStart, dateEnd, timeStart, timeEnd;
		const validJob = JSON.parse(
			JSON.stringify(job),
			this.timeService.parseDateFromJSON
		);

		if (this.validationService.isStringEmpty(validJob.name)) {
			this.messageService.showToaster('e_3', 'general', 'Job Name');
			return false;
		}

		const primaryContact = Object.assign({}, clients[0]);
		const isPrimaryContact = this.isContact(primaryContact);

		if (isPrimaryContact) {
			validJob.primaryContact = primaryContact;
		} else {
			this.messageService.showToaster('e_1', 'job');
			return false;
		}

		const secondaryContact = Object.assign({}, clients[1]);
		const isSecondaryContact = this.isContact(secondaryContact);

		if (isSecondaryContact) {
			if (this.isContactsEqual(secondaryContact, primaryContact)) {
				return false;
			}
			validJob.secondaryContact = secondaryContact;
		} else {
			delete validJob.secondaryContact;
		}

		validJob.notes = this.emailService.addMaiStyling(validJob.notes);
		if (!validJob.id && validJob.workflow && validJob.workflow.id === -1) {
			validJob.workflow.id = null;
		}

		if (validJob.hasNoDate) {
			validJob.startDate = null;
			validJob.finishDate = null;
			validJob.startTime = null;
			validJob.finishTime = null;
			if (jobId) {
				validJob.id = jobId;
				delete validJob.jobId;
			}
			if (leadId) {
				return {
					id: leadId,
					jobDTO: validJob
				};
			}
			return {
				id: validJob.id ? validJob.id : null,
				jobDTO: validJob
			};
		}

		if (!validJob.startDate || !validJob.finishDate) {
			this.messageService.showToaster('e_1', 'appointment');
			return false;
		}

		dateStart = this.timeService.getDateInFormat(validJob.startDate, 'date');
		dateEnd = this.timeService.getDateInFormat(validJob.finishDate, 'date');

		const isCorrectDate = this.timeService.compareDates(dateEnd, dateStart);

		if (isCorrectDate) {
			this.messageService.showToaster('e_0', 'job');
			return false;
		}

		if (!validJob.allday) {
			const isStartIn24Format = this.timeService.isIn24Format(
				validJob.startTime
			);
			const isEndIn24Format = this.timeService.isIn24Format(
				validJob.finishTime
			);
			timeStart = isStartIn24Format
				? validJob.startTime
				: this.timeConverter.convert12to24(validJob.startTime);
			timeEnd = isEndIn24Format
				? validJob.finishTime
				: this.timeConverter.convert12to24(validJob.finishTime);

			if (!timeStart || !timeEnd) {
				this.messageService.showToaster('e_3', 'job');
				return false;
			}

			if (dateStart === dateEnd) {
				const isCorrectTime = this.timeService.compareTimes(timeStart, timeEnd);

				if (!isCorrectTime) {
					this.messageService.showToaster('e_4', 'job');
					return false;
				}
			}
		}

		validJob.startDate = dateStart;
		validJob.finishDate = dateEnd;
		validJob.startTime = timeStart;
		validJob.finishTime = timeEnd;
		if (jobId) {
			validJob.id = jobId;
			delete validJob.jobId;
		}

		if (leadId) {
			return {
				id: leadId,
				jobDTO: validJob
			};
		}
		return {
			id: validJob.id ? validJob.id : null,
			jobDTO: validJob
		};
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/lead/lead.service.ts